/**
 * Document Intelligence — Documents Master (list).
 *
 * The document categories, each with the fields worth extracting from it. When
 * an upload is processed, the LLM picks the category from these and extracts
 * exactly that category's fields — so this list is the extraction schema.
 *
 * Reads through documentCategoriesService (`/api/v1/document-schemas`). The
 * empty state can be previewed with `?empty=1` in the URL.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tag } from 'sharedUi/Tag';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { formatDate } from 'sharedUi/dateUtils';
import PermissionGuard from '../../components/guards/PermissionGuard';
import PageHeader from '../../components/common/page-header';
import { Icon } from '../../components/common/icon';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useDebounce } from '../../hooks/useDebounce';
import { useCanCreate, useCanDelete, useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { extractErrorMessage } from '../../utils/error-handler';
import { documentCategoriesService } from '../../services/document-categories.service';
import {
  DOCUMENT_LANGUAGE_FILTER_OPTIONS,
  languageLabel,
  type DocumentCategory,
  type DocumentLanguage,
} from '../../types/document-intelligence.types';
import { DOCUMENT_MASTER_BASE_PATH } from './paths';

const DocumentMasterList = () => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'document-category',
    basePath: DOCUMENT_MASTER_BASE_PATH,
  });
  // Gated on AI Models for now, like the rest of Document Intelligence, until
  // the module has permissions of its own.
  const canCreate = useCanCreate(JOB_NAMES.AI_MODEL_MASTER);
  const canUpdate = useCanUpdate(JOB_NAMES.AI_MODEL_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.AI_MODEL_MASTER);
  const toast = useRef<Toast>(null);
  const [searchParams] = useSearchParams();
  const previewEmpty = searchParams.get('empty') === '1';

  const [rows, setRows] = useState<DocumentCategory[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>('category');
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(1);
  const [language, setLanguage] = useState<DocumentLanguage | ''>('');
  const [toDelete, setToDelete] = useState<DocumentCategory | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await documentCategoriesService.list({
        search: debouncedSearch || undefined,
        page: Math.floor(first / pageSize) + 1,
        page_size: pageSize,
        sort_by: sortField || undefined,
        sort_order: sortOrder === -1 ? 'desc' : 'asc',
        language: language || undefined,
      });
      setRows(previewEmpty ? [] : res.data);
      setTotalRecords(previewEmpty ? 0 : res.pagination.totalItems);
    } catch (err) {
      toast.current?.show({
        severity: 'error', summary: 'Error', life: 10000,
        detail: extractErrorMessage(err, 'Failed to load document categories.'),
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, first, pageSize, sortField, sortOrder, previewEmpty, language]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      setDeleting(true);
      await documentCategoriesService.delete(toDelete._id);
      toast.current?.show({ severity: 'success', summary: 'Deleted', life: 3000,
        detail: `"${toDelete.category}" was deleted.` });
      setToDelete(null);
      fetchRows();
    } catch (err) {
      setToDelete(null);
      toast.current?.show({ severity: 'error', summary: 'Error', life: 10000,
        detail: extractErrorMessage(err, 'Failed to delete the category.') });
    } finally {
      setDeleting(false);
    }
  };

  const columns: SaasColumn<DocumentCategory>[] = useMemo(() => [
    {
      field: 'category',
      header: 'Category',
      sortable: true,
      hideable: false,
      body: (c) => (
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="file-text" size={16} className="shrink-0 text-ink-muted" />
          <span className="truncate font-medium text-ink" title={c.category}>{c.category}</span>
        </div>
      ),
    },
    {
      field: 'language',
      header: 'Language',
      body: (c) => languageLabel(c.language),
    },
    {
      field: 'fieldCount',
      header: 'Fields',
      sortable: true,
      align: 'right',
      body: (c) => c.fields.length,
    },
    {
      field: 'requiredCount',
      header: 'Required',
      align: 'right',
      body: (c) => c.fields.filter((fld) => fld.required).length,
    },
    {
      field: 'isActive',
      header: 'Status',
      sortable: true,
      body: (c) => (c.isActive
        ? <Tag value="Active" severity="success" />
        : <Tag value="Inactive" severity="secondary" />),
    },
    {
      field: 'updatedAt',
      header: 'Last Updated',
      sortable: true,
      body: (c) => formatDate(c.updatedAt || c.createdAt || ''),
    },
    {
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (c) => (
        <div className="flex gap-1 justify-center">
          <button aria-label="View" type="button"
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            onClick={() => navigateToView(c._id)}
            data-testid="DocumentMasterList.Action.View">
            <i className="pi pi-eye text-sm text-green-600 dark:text-green-400" style={{ fontSize: '1.125rem' }} />
          </button>
          {canUpdate && (
            <button aria-label="Edit" type="button"
              className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              onClick={() => navigateToEdit(c._id)}
              data-testid="DocumentMasterList.Action.Edit">
              <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.125rem' }} />
            </button>
          )}
          {canDelete && (
            <button aria-label="Delete" type="button"
              className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              onClick={() => setToDelete(c)}
              data-testid="DocumentMasterList.Action.Delete">
              <i className="pi pi-trash text-red-600" style={{ fontSize: '1.125rem' }} />
            </button>
          )}
        </div>
      ),
    },
  ], [canUpdate, canDelete, navigateToView, navigateToEdit]);

  // "Nothing exists" (no search or filter, nothing came back) gets its own
  // screen; a search or language filter that matches nothing keeps the table —
  // and the filter, which is the thing to change.
  const nothingYet = !loading && totalRecords === 0 && !debouncedSearch && !language;

  return (
    <PermissionGuard jobName={JOB_NAMES.AI_MODEL_MASTER} resourceName="Documents Master">
      <Toast ref={toast} position="top-right" />
      <div className="p-3" data-testid="SCR-DocumentIntelligence-Master-List">
        <PageHeader
          icon="pi pi-list"
          title="Documents Master"
          subtitle="Document categories and the fields to extract from each"
          testId="DocumentMaster.Header"
        />

        {nothingYet ? (
          <div
            className="flex flex-col items-center justify-center gap-3 rounded-xl border border-line bg-surface px-6 py-16 text-center shadow-sm"
            data-testid="DocumentMaster.Empty"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-primary dark:bg-indigo-900/30">
              <Icon name="list" size={24} />
            </span>
            <div className="flex flex-col gap-1">
              <p className="text-heading font-semibold text-ink">No document categories yet</p>
              <p className="text-body text-ink-muted max-w-md">
                Add a category — such as Transfer Order — and the fields to extract from it.
                Uploaded documents are classified into these categories.
              </p>
            </div>
            {canCreate && (
              <Button label="Create Category" icon="pi pi-plus" onClick={() => navigateToCreate()}
                testId="DocumentMaster.Button.CreateFirst" />
            )}
          </div>
        ) : (
          <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
            <SaasDataTable
              tableId="document-master"
              columns={columns}
              data={rows}
              loading={loading}
              dataKey="_id"
              emptyMessage={language
                ? `No ${languageLabel(language)} categories match`
                : 'No categories match your search'}
              first={first}
              rows={pageSize}
              totalRecords={totalRecords}
              onPage={(e) => { setFirst(e.first); setPageSize(e.rows); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sortField={sortField || undefined}
              sortOrder={sortOrder || undefined}
              onSort={(e) => { setSortField(e.sortField); setSortOrder(e.sortOrder); setFirst(0); }}
              onFilter={() => {}}
              globalSearch={search}
              onGlobalSearchChange={(v) => { setSearch(v); setFirst(0); }}
              globalSearchPlaceholder="Search categories or field names..."
              rowClassName={() => 'cursor-pointer'}
              onRowClick={(c) => navigateToView(c._id)}
              testId="DocumentMasterList"
              toolbarEnd={
                <>
                  <Dropdown
                    value={language}
                    options={DOCUMENT_LANGUAGE_FILTER_OPTIONS}
                    optionLabel="label" optionValue="value"
                    style={{ minWidth: 160 }}
                    onChange={(e: { value: DocumentLanguage | '' }) => {
                      setLanguage(e.value || '');
                      setFirst(0);
                    }}
                    data-testid="DocumentMaster.Filter.Language"
                  />
                  <span className="text-sm text-ink-muted whitespace-nowrap">
                    {totalRecords} {totalRecords === 1 ? 'category' : 'categories'}
                  </span>
                  <RefreshButton onClick={fetchRows} loading={loading} />
                  {canCreate && (
                    <Button label="Create Category" icon="pi pi-plus" size="small"
                      onClick={() => navigateToCreate()} testId="DocumentMaster.Button.Create" />
                  )}
                </>
              }
            />
          </div>
        )}

        <DeleteConfirmDialog
          visible={!!toDelete}
          onCancel={() => setToDelete(null)}
          onDelete={confirmDelete}
          message={`Delete the category "${toDelete?.category ?? ''}"? Documents will no longer be classified into it.`}
          loading={deleting}
          testId="DocumentMasterList.Dialog.Delete"
        />
      </div>
    </PermissionGuard>
  );
};

export default DocumentMasterList;
